"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, type OfflinePhoto } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

/**
 * Hook pour la gestion des photos d'une mesure (CRUD offline).
 * Les photos sont stockées en blob dans IndexedDB via Dexie.
 */
export function usePhotos(mesureId: string) {
  const photos =
    useLiveQuery(
      () =>
        offlineDb.photos
          .where("mesureId")
          .equals(mesureId)
          .toArray(),
      [mesureId]
    ) ?? [];

  /** Ajoute une photo compressée à la mesure */
  async function addPhoto(blob: Blob, fileName: string): Promise<string> {
    const id = crypto.randomUUID();

    await offlineDb.transaction("rw", offlineDb.photos, offlineDb.mesures, async () => {
      // Stocker le blob dans la table photos
      await offlineDb.photos.add({
        id,
        mesureId,
        blob,
        fileName,
        syncStatus: "pending",
      });

      // Ajouter l'id dans le tableau mesure.photos
      const mesure = await offlineDb.mesures.get(mesureId);
      if (mesure) {
        await offlineDb.mesures.update(mesureId, {
          photos: [...mesure.photos, id],
          updatedAt: new Date(),
          syncStatus: "pending",
        });
      }
    });

    return id;
  }

  /** Supprime une photo côté serveur puis localement.
   *  Verrouille la sync pour éviter qu'un pull concurrent ne recrée la photo. */
  async function deletePhoto(photoId: string) {
    await deleteWithOfflineFallback("photo", photoId, `/api/photos/${photoId}`, async () => {
      await offlineDb.transaction("rw", offlineDb.photos, offlineDb.mesures, async () => {
        await offlineDb.photos.delete(photoId);

        const mesure = await offlineDb.mesures.get(mesureId);
        if (mesure) {
          await offlineDb.mesures.update(mesureId, {
            photos: mesure.photos.filter((id) => id !== photoId),
            updatedAt: new Date(),
            syncStatus: "pending",
          });
        }
      });
    });
  }

  /** Remplace le blob d'une photo existante (après crop) */
  async function updatePhotoBlob(photoId: string, newBlob: Blob) {
    await offlineDb.photos.update(photoId, {
      blob: newBlob,
      syncStatus: "pending",
    });
  }

  return { photos, addPhoto, deletePhoto, updatePhotoBlob };
}

export type { OfflinePhoto };
